export type QualityName = "standard" | "high" | "cinematic";

export interface Parameters {
  exposure: number;
  bloomStrength: number;
  bloomRadius: number;
  bloomThreshold: number;
  chromaticAberration: number;
  vignette: number;
  filmGrain: number;
  cameraFov: number;
  cameraDistance: number;
  integrationStep: number;
  maximumSteps: number;
  diskInnerRadius: number;
  diskOuterRadius: number;
  diskTemperature: number;
  emissivityFalloff: number;
  accretionRate: number;
  turbulenceScale: number;
  turbulenceStrength: number;
  diskRotationSpeed: number;
  starDensity: number;
  galaxyIntensity: number;
}

export interface AppState {
  version: 1;
  parameters: Parameters;
  quality: QualityName;
  debugView: number;
  hudVisible: boolean;
  cinematic: boolean;
  paused: boolean;
  audioEnabled: boolean;
  audioVolume: number;
  preset: string;
  seed: number;
}

export interface QualityProfile {
  scale: number;
  minScale: number;
  dprCap: number;
  maximumSteps: number;
  bloomScale: number;
}

export const QUALITY_PROFILES: Record<QualityName, QualityProfile> = {
  standard: { scale: 0.7, minScale: 0.45, dprCap: 1.25, maximumSteps: 96, bloomScale: 0.35 },
  high: { scale: 0.9, minScale: 0.55, dprCap: 1.75, maximumSteps: 160, bloomScale: 0.45 },
  cinematic: { scale: 1, minScale: 0.6, dprCap: 2, maximumSteps: 256, bloomScale: 0.5 }
};

export const PARAMETER_META = {
  exposure: ["Exposure", 0.5, 2.5, 0.01],
  bloomStrength: ["Bloom strength", 0, 2.5, 0.01],
  bloomRadius: ["Bloom radius", 0, 1, 0.01],
  bloomThreshold: ["Bloom threshold", 0, 2, 0.01],
  chromaticAberration: ["Chromatic aberration", 0, 0.006, 0.0001],
  vignette: ["Vignette", 0, 1, 0.01],
  filmGrain: ["Film grain", 0, 0.15, 0.001],
  cameraFov: ["Camera FOV", 25, 90, 1],
  cameraDistance: ["Camera distance", 7, 60, 0.1],
  integrationStep: ["Integration step", 0.015, 0.12, 0.001],
  maximumSteps: ["Maximum steps", 64, 320, 1],
  diskInnerRadius: ["Disk inner radius", 4.5, 10, 0.1],
  diskOuterRadius: ["Disk outer radius", 12, 60, 0.5],
  diskTemperature: ["Disk temperature", 0.4, 2, 0.01],
  emissivityFalloff: ["Emissivity falloff", 1, 4, 0.01],
  accretionRate: ["Accretion rate", 0, 2, 0.01],
  turbulenceScale: ["Turbulence scale", 0.5, 10, 0.1],
  turbulenceStrength: ["Turbulence strength", 0, 1.2, 0.01],
  diskRotationSpeed: ["Disk rotation speed", 0, 1.5, 0.01],
  starDensity: ["Star density", 0, 2, 0.01],
  galaxyIntensity: ["Galaxy intensity", 0, 2, 0.01]
} as const satisfies Record<keyof Parameters, readonly [string, number, number, number]>;

export const DEFAULT_PARAMETERS: Parameters = {
  exposure: 1.15,
  bloomStrength: 0.9,
  bloomRadius: 0.55,
  bloomThreshold: 0.8,
  chromaticAberration: 0.0012,
  vignette: 0.32,
  filmGrain: 0.035,
  cameraFov: 50,
  cameraDistance: 18,
  integrationStep: 0.045,
  maximumSteps: 160,
  diskInnerRadius: 6,
  diskOuterRadius: 28,
  diskTemperature: 1,
  emissivityFalloff: 2.6,
  accretionRate: 1,
  turbulenceScale: 3.5,
  turbulenceStrength: 0.45,
  diskRotationSpeed: 0.35,
  starDensity: 0.8,
  galaxyIntensity: 0.65
};

const STORAGE_KEY = "gargantua:state:v1";

export function isMobileDevice(): boolean {
  return matchMedia("(pointer: coarse)").matches || innerWidth < 760;
}

export function createDefaultState(): AppState {
  const quality: QualityName = isMobileDevice() ? "standard" : "high";
  return {
    version: 1,
    parameters: { ...DEFAULT_PARAMETERS, maximumSteps: QUALITY_PROFILES[quality].maximumSteps },
    quality,
    debugView: 0,
    hudVisible: true,
    cinematic: !isMobileDevice() && !matchMedia("(prefers-reduced-motion: reduce)").matches,
    paused: false,
    audioEnabled: false,
    audioVolume: 0.35,
    preset: "observer",
    seed: 42
  };
}

function clamp(value: number, min: number, max: number): number {
  return Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;
}

export function sanitizeState(input: Partial<AppState>, fallback = createDefaultState()): AppState {
  const result: AppState = structuredClone(fallback);
  if (input.quality && input.quality in QUALITY_PROFILES) result.quality = input.quality;
  if (typeof input.debugView === "number") result.debugView = Math.round(clamp(input.debugView, 0, 9));
  if (typeof input.hudVisible === "boolean") result.hudVisible = input.hudVisible;
  if (typeof input.cinematic === "boolean") result.cinematic = input.cinematic;
  if (typeof input.paused === "boolean") result.paused = input.paused;
  if (typeof input.audioEnabled === "boolean") result.audioEnabled = input.audioEnabled;
  if (typeof input.audioVolume === "number") result.audioVolume = clamp(input.audioVolume, 0, 1);
  if (typeof input.preset === "string") result.preset = input.preset;
  if (typeof input.seed === "number") result.seed = Math.round(clamp(input.seed, 0, 1_000_000));
  const source: Partial<Parameters> = input.parameters ?? {};
  for (const key of Object.keys(PARAMETER_META) as (keyof Parameters)[]) {
    const meta = PARAMETER_META[key];
    const value = source[key];
    if (typeof value === "number") result.parameters[key] = clamp(value, meta[1], meta[2]);
  }
  if (result.parameters.diskOuterRadius <= result.parameters.diskInnerRadius + 1) {
    result.parameters.diskOuterRadius = result.parameters.diskInnerRadius + 1;
  }
  return result;
}

export function loadState(): AppState {
  const fallback = createDefaultState();
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") as Partial<AppState> | null;
    return parsed?.version === 1 ? sanitizeState(parsed, fallback) : fallback;
  } catch {
    return fallback;
  }
}

export function saveState(state: AppState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage is optional; rendering must remain available.
  }
}

export function applyUrlState(state: AppState, search = location.search): AppState {
  const params = new URLSearchParams(search);
  const patch: Partial<AppState> = {};
  const quality = params.get("quality");
  if (quality === "standard" || quality === "high" || quality === "cinematic") patch.quality = quality;
  const debug = Number(params.get("debug"));
  if (Number.isFinite(debug)) patch.debugView = debug;
  const seed = Number(params.get("seed"));
  if (Number.isFinite(seed)) patch.seed = seed;
  const preset = params.get("preset");
  if (preset) patch.preset = preset;
  if (params.get("hud") === "0") patch.hudVisible = false;
  if (params.get("cinematic") === "0") patch.cinematic = false;
  return sanitizeState(patch, state);
}
